import type { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { type ByIdsResultJSON, byIdsResultJSONZ, parseByIdsResult } from "_shared/_api/by-ids/by-ids-result";
import PaginatedResponse from "_shared/_api/pagination/responses/paginated-response";
import { reportSchemaFailure } from "_shared/_errors/client-error-reporter";
import { api } from "_shared/_stores/api";
import { ResourceType } from "permissions/resources/permission-resource-types";
import { z } from "zod";

/**
 * The names of the records per-record grants are on, for the permissions pages, from the business
 * centre that owns each record. Names live there, not in the Permissions API.
 *
 * Every centre answers a bulk read with `ByIdsResult<T>`: each id comes back found, or unavailable as
 * `missing` (it names nothing) or `unauthorized` (it exists and the viewer may not read it). This
 * read keeps both reasons and narrows each found record to its id and name, so a row can say
 * "Conversation no longer exists" apart from "No access to this conversation".
 */

/**
 * When a record was opened, and by whom, where its centre's by-ids read says so: a conversation
 * carries both, a proposal only the day, and the other kinds neither.
 */
export type RecordOpening = Readonly<{ date: string; byUserId?: string }>;

export type PermissionTarget = Readonly<{ id: string; name: string; opened?: RecordOpening }>;

export type PermissionTargetsArgs = Readonly<{
  /** A per-record kind's resource type, such as `ForumAccess`. */
  resourceType: string;
  ids: readonly string[];
}>;

/**
 * Where one resource type's records are read by id, which property names them, and, for a centre
 * whose read carries it, when and by whom the record was opened.
 */
type TargetSource = Readonly<{ url: string; nameProperty: string; opened?: (record: Readonly<Record<string, unknown>>) => RecordOpening | undefined }>;

/** A conversation's opening: its `createdDate`, and the `creator` subscriber's user id. */
function forumOpening(record: Readonly<Record<string, unknown>>): RecordOpening | undefined {
  if (typeof record.createdDate !== "string") return undefined;
  const creator = record.creator;
  const byUserId = creator && typeof creator === "object" ? (creator as Record<string, unknown>).userId : undefined;
  return { date: record.createdDate, byUserId: typeof byUserId === "string" ? byUserId : undefined };
}

/** A proposal's opening: its `createdDate` alone, since the barebones read names no creator. */
function proposalOpening(record: Readonly<Record<string, unknown>>): RecordOpening | undefined {
  return typeof record.createdDate === "string" ? { date: record.createdDate } : undefined;
}

const TARGET_SOURCES: Readonly<Partial<Record<string, TargetSource>>> = {
  [ResourceType.ForumAccess]: { url: "messaging/forums/by-ids", nameProperty: "name", opened: forumOpening },
  [ResourceType.DocumentAccess]: { url: "documents/get-permission-targets-by-ids", nameProperty: "name" },
  [ResourceType.ProposalAccess]: { url: "work/proposals/get-by-ids", nameProperty: "name", opened: proposalOpening },
  [ResourceType.WorkAgreementAccess]: { url: "work/get-by-ids", nameProperty: "name" },
  [ResourceType.EntityAgreementAccess]: {
    url: "legal-entities/entity-agreements/get-permission-targets-by-ids",
    nameProperty: "entityName",
  },
  [ResourceType.FeeScheduleTemplateAccess]: { url: "work/fee-schedule-templates/get-by-ids", nameProperty: "name" },
  [ResourceType.TeamTemplateAccess]: { url: "work/team-templates/get-by-ids", nameProperty: "name" },
  [ResourceType.CalendarEventAccess]: { url: "calendar/events/get-by-ids", nameProperty: "name" },
};

/**
 * Where each per-record kind's records are found by name: the centre's admin-gated permission-targets
 * search (#6174). The domain lists answer only what the viewer may read, which is the wrong gate for
 * an administrator choosing a record to manage; these answer the organization's records to anyone
 * holding entity-wide member permissions, and only their id, name and resource type.
 */
const SEARCH_SOURCES: Readonly<Partial<Record<string, string>>> = {
  [ResourceType.ForumAccess]: "messaging/forums/permission-targets",
  [ResourceType.DocumentAccess]: "documents/permission-targets",
  [ResourceType.ProposalAccess]: "work/proposals/permission-targets",
  [ResourceType.WorkAgreementAccess]: "work/permission-targets",
  [ResourceType.EntityAgreementAccess]: "legal-entities/entity-agreements/permission-targets",
  [ResourceType.FeeScheduleTemplateAccess]: "work/fee-schedule-templates/permission-targets",
  [ResourceType.TeamTemplateAccess]: "work/team-templates/permission-targets",
  [ResourceType.CalendarEventAccess]: "calendar/events/permission-targets",
};

/** The longest text a permission-targets search takes, as the API checks it. */
export const PERMISSION_TARGET_SEARCH_MAXIMUM_LENGTH = 200;

/** Whether records of this resource type can be found by name. */
export function canSearchTargetsOf(resourceType: string): boolean {
  return SEARCH_SOURCES[resourceType] !== undefined;
}

export type PermissionTargetSearchArgs = Readonly<{
  /** The per-record kinds to search, each in its own centre. */
  resourceTypes: readonly string[];
  /** Text the name contains, matched case-insensitively. */
  query: string;
  /** The most records to answer for each kind. */
  pageSize: number;
}>;

/** A record found by name, with the per-record kind a grant on it carries. */
export type FoundPermissionTarget = PermissionTarget & Readonly<{ resourceType: string }>;

export type PermissionTargetSearchResult = Readonly<{
  /** Each kind's first page of matches, in the order the kinds were asked for. */
  targets: readonly FoundPermissionTarget[];
  /** How many records of each kind match in all, past the page. */
  totals: Readonly<Record<string, number>>;
  /** The kinds whose search failed. The others still answer: one centre down does not empty the list. */
  failed: readonly string[];
}>;

const FoundTargetsZ = z.array(z.object({ id: z.string(), name: z.string(), resourceType: z.string() }));

/** The part of RTK Query's base query a search uses: a GET with parameters, and the response for its headers. */
type BaseQueryLike = (arg: { url: string; params: Record<string, string | number> }) => Promise<{
  data?: unknown;
  error?: FetchBaseQueryError;
  meta?: { response?: Response };
}>;

/** One kind's search, as one request to its centre. */
async function searchOneKind(
  resourceType: string,
  args: PermissionTargetSearchArgs,
  baseQuery: BaseQueryLike,
) {
  const url = SEARCH_SOURCES[resourceType];
  if (!url) return { resourceType, failed: true as const };
  const result = await baseQuery({ url, params: { query: args.query.trim(), pageIndex: 0, pageSize: args.pageSize } });
  if (result.error) return { resourceType, failed: true as const };
  const parsed = FoundTargetsZ.safeParse(result.data);
  if (!parsed.success) {
    reportSchemaFailure({
      schemaName: "PermissionTargetSearch",
      operation: `searchPermissionTargets(${resourceType})`,
      phase: "response",
      fields: parsed.error.issues.map((issue) => issue.path.map(String).join(".")),
    });
    return { resourceType, failed: true as const };
  }
  const header = result.meta?.response?.headers.get("x-pagination") ?? "";
  const total = header ? PaginatedResponse.parsePaginationHeader(header).totalElements : parsed.data.length;
  // Labelled with the kind asked for: the centre's answer says the same, and this is what the page links by.
  return { resourceType, failed: false as const, total, targets: parsed.data.map((target) => ({ id: target.id, name: target.name, resourceType })) };
}

/** Whether the permissions pages can name the records of this resource type. */
export function canNameTargetsOf(resourceType: string): boolean {
  return TARGET_SOURCES[resourceType] !== undefined;
}

const TargetsResultZ = byIdsResultJSONZ(z.looseObject({ id: z.string() }));

/** A found record's name, or its id when the centre sent no name at all, which none should. */
function nameOf(record: Readonly<Record<string, unknown>>, property: string): string {
  const name = record[property];
  return typeof name === "string" && name.trim() ? name : String(record.id);
}

export const permissionTargetsApi = api.injectEndpoints({
  endpoints: (build) => ({
    getPermissionTargetsByIds: build.query<ByIdsResultJSON<PermissionTarget>, PermissionTargetsArgs>({
      serializeQueryArgs: ({ queryArgs }) => `${queryArgs.resourceType}:${[...queryArgs.ids].sort().join(",")}`,
      queryFn: async ({ resourceType, ids }, _api, _extraOptions, baseQuery) => {
        const source = TARGET_SOURCES[resourceType];
        if (!source) {
          return { error: { status: "CUSTOM_ERROR", error: `No record names for ${resourceType}.` } satisfies FetchBaseQueryError };
        }
        if (ids.length === 0) return { data: { found: [], unavailable: [] } };

        const result = await baseQuery({ url: source.url, method: "POST", body: [...ids] });
        if (result.error) return { error: result.error };

        const parsed = parseByIdsResult(TargetsResultZ, result.data, {
          schemaName: "PermissionTargetsByIds",
          operation: `getPermissionTargetsByIds(${resourceType})`,
        });
        if ("error" in parsed) return { error: parsed.error };
        return {
          data: {
            found: parsed.data.found.map((record) => ({
              id: record.id,
              name: nameOf(record, source.nameProperty),
              opened: source.opened?.(record),
            })),
            unavailable: parsed.data.unavailable,
          },
        };
      },
      providesTags: [{ type: "Permission" as const, id: "TARGETS" }],
    }),
    searchPermissionTargets: build.query<PermissionTargetSearchResult, PermissionTargetSearchArgs>({
      serializeQueryArgs: ({ queryArgs }) =>
        `${[...queryArgs.resourceTypes].join(",")}:${queryArgs.pageSize}:${queryArgs.query.trim().toLowerCase()}`,
      queryFn: async (args, _api, _extraOptions, baseQuery) => {
        const answers = await Promise.all(args.resourceTypes.map((resourceType) => searchOneKind(resourceType, args, baseQuery as BaseQueryLike)));
        const totals: Record<string, number> = {};
        const targets: FoundPermissionTarget[] = [];
        const failed: string[] = [];
        for (const answer of answers) {
          if (answer.failed) {
            failed.push(answer.resourceType);
            continue;
          }
          totals[answer.resourceType] = answer.total;
          targets.push(...answer.targets);
        }
        return { data: { targets, totals, failed } };
      },
      providesTags: [{ type: "Permission" as const, id: "TARGETS" }],
    }),
  }),
});

export const { useGetPermissionTargetsByIdsQuery, useSearchPermissionTargetsQuery } = permissionTargetsApi;
